RivalIQ.module('configuration', function (module, App, Backbone, Marionette, $, _, window, enums) {

    'use strict';

    const configurations = {};
    const menus = module.menus = {};

    /**
     * Determines whether a menu item is active from the window location.
     * @param [location]
     * @returns {boolean}
     */
    const activateByLocation = menus.activateByLocation = function (location) {
        const me = this;
        const navigation = me.navigation;

        // NOTE if no navigation was defined then we don't
        // know how to activate this particular item
        if (!navigation) {

            return false;
        }

        let isRootUrl = false;
        const url = _.result(navigation, 'url');
        const baseUrl = _.result(navigation, 'baseUrl');
        let resolvedUrl = url;

        // NOTE the regex is kind of a trump card, an all access pass to the
        // path for item activation - speak softly, and carry a big stick
        const regEx = _.result(navigation, 'regex');
        const isRegEx = (regEx instanceof RegExp);

        let path = (location || window.location).href.replace(RivalIQ.state.product.get('urlBasepath'), '');
        path = path.split('?')[0];

        if (baseUrl) {

            isRootUrl = (url === '#');
            resolvedUrl = baseUrl + url;
        }

        // NOTE if we matched the path, or we had a regular expression that
        // matched, or we matched the root (without an optionally configured
        // default #)...then eureka
        return (path === resolvedUrl || (isRegEx && Boolean(path.match(regEx))) || (isRootUrl && path === baseUrl));
    };

    /**
     * Creates an activating menu item.
     * @param properties
     * @returns {*}
     */
    function createActivatingItem(properties) {

        return _.extend({
            menuItemActive: activateByLocation
        }, properties || {});
    }

    /**
     * Creates an item with the specified properties.
     * @param name
     * @param url
     * @param baseUrl
     * @param navOptions
     * @param options
     * @returns {*}
     */
    function createItem(name, url, baseUrl, navOptions, options) {

        const navHash = _.extend({
            url: url,
            baseUrl: baseUrl
        }, navOptions);
        return createActivatingItem(_.extend({
            name: name,
            navigation: navHash
        }, options));
    }

    /**
     * Creates a conditionally displayed item with the specified properties.
     * @param name
     * @param url
     * @param baseUrl
     * @param navOptions
     * @param additionalAttributes
     * @returns {*}
     */
    function createConditionalItem(name, url, baseUrl, navOptions, additionalAttributes) {

        const conditions = _.drop(arguments, 5);

        return createItem(name, url, baseUrl, navOptions, _.extend({
            display: _.partial(_.every, conditions, function (fn) {
                return fn();
            })
        }, additionalAttributes));
    }

    /**
     * Creates a configuration with the specified properties.
     * @param name
     * @param metadata
     * @param items
     * @returns {{name: *, metadata: *, items: *}}
     */
    function createConfiguration(name, metadata, items) {

        const config = {
            name: name,
            metadata: metadata,
            items: items
        };

        return config;
    }

    function createMenuItem(source) {
        const details = enums.sourceDetails[source] || source;
        return {
            display: details.name,
            value: details.channel
        };
    }

    function addSourcesToConfiguration(configuration) {
        const sources = [
            {
                name: 'Cross-Channel',
                channel: 'cross_channel'
            },
            enums.sources.TWITTER,
            enums.sources.FACEBOOK,
            enums.sources.YOUTUBE,
            enums.sources.INSTAGRAM,
            enums.sources.TIKTOK
        ];
        configuration.menuItems = _.map(sources, createMenuItem);
    }

    /**
     * Converts a source to configuration.
     * @param source
     * @param addendum
     * @returns {{name: *, metadata: *, items: *}}
     */
    function sourceToConfiguration(source, addendum) {

        const details = enums.sourceDetails[source];
        const channel = details.channel;

        let items = [
            createItem('Overview', `/${channel}`, '/overview', {}),
            createItem('Leaderboard', `/${channel}`, '/leaderboard', {
                regex: new RegExp(`/leaderboard/${channel}`, 'i')
            }),
            createItem('Social Posts', `/${channel}`, '/posts', {
                regex: new RegExp(`/posts/${channel}`, 'i')
            }, {
                id: 'social-posts-subnav-link'
            }),
            createItem('Post Tags', `/${channel}`, '/post-tags', {
                regex: new RegExp(`/post-tags/${channel}`, 'i')
            }),
            createItem('Posted URLs', `/${channel}`, '/posted-urls', {
                regex: new RegExp(`/posted-urls/${channel}`, 'i')
            })
        ];

        items = items.concat(addendum || []);

        const metadata = {
            source: source,
            channel: channel,
            details: details
        };

        const configuration = createConfiguration(details.name, metadata, items);

        addSourcesToConfiguration(configuration);
        return configuration;
    }

    configurations.TWITTER = sourceToConfiguration(enums.sources.TWITTER);

    configurations.FACEBOOK = sourceToConfiguration(enums.sources.FACEBOOK);

    const facebookInsightsBaseUrl = /^\/insights\/facebook\/\d+/;
    configurations.FACEBOOK_INSIGHTS = createConfiguration('Facebook Insights', {
        channel: 'facebook_insights'
    }, [
        createItem('Page', '', facebookInsightsBaseUrl),
        createItem('Posts', '/posts', facebookInsightsBaseUrl, {
            regex: /\/(posts|reels)/i
        })
    ]);

    const twitterInsightsBaseUrl = /^\/insights\/twitter\/\d+/;
    configurations.TWITTER_INSIGHTS = createConfiguration('Twitter Analytics', {
        channel: 'twitter_insights'
    }, [
        createItem('Profile', '', twitterInsightsBaseUrl),
        createItem('Tweets', '/posts', twitterInsightsBaseUrl, {
            regex: /\/posts/i
        })
    ]);

    const instaInsightsBaseUrl = /^\/insights\/instagram\/\d+/;
    configurations.INSTAGRAM_INSIGHTS = createConfiguration('Instagram Insights', {
        channel: 'instagram_insights'
    }, [
        createItem('Profile', '', instaInsightsBaseUrl),
        createItem('Posts', '/posts', instaInsightsBaseUrl, {
            regex: /\/posts/i
        }),
        createItem('Stories', '/stories', instaInsightsBaseUrl, {
            regex: /\/stories/i
        })
    ]);

    configurations.LINKEDIN = createConfiguration('LinkedIn Analytics');

    // This is relying on the old config pointing to configurations.LINKEDIN and the new beta pointing at configurations.LINKEDIN_INSIGHTS
    const linkedInInsightsBaseUrl = /^\/insights\/linkedin\/\d+/;
    configurations.LINKEDIN_INSIGHTS = createConfiguration('LinkedIn Analytics', {
        channel: 'linkedin'
    }, [
        createItem('Page', '', linkedInInsightsBaseUrl),
        createItem('Updates', '/posts', linkedInInsightsBaseUrl, {
            regex: /\/posts/i
        })
    ]);

    const tikTokInsightsBaseUrl = /^\/insights\/tiktok\/\d+/;
    configurations.TIKTOK_INSIGHTS = createConfiguration('TikTok Insights', {
        channel: 'tiktok_insights'
    }, [
        createItem('Profile', '', tikTokInsightsBaseUrl),
        createItem('Videos', '/posts', tikTokInsightsBaseUrl, {
            regex: /\/posts/i
        })
    ]);

    configurations.YOUTUBE = sourceToConfiguration(enums.sources.YOUTUBE);
    configurations.INSTAGRAM = sourceToConfiguration(enums.sources.INSTAGRAM);
    configurations.TIKTOK = sourceToConfiguration(enums.sources.TIKTOK);

    // NOTE though we don't need to specify '#' as the URL for
    // pages that do not have hash state we should so that if the
    // user clicks on the menu again we don't reload the page
    const crossChannelItems = [
        createItem('Overview', '/overview', '', {
            regex: /\/overview/i
        }),
        createItem('Leaderboard', '/leaderboard', '', {
            regex: /\/leaderboard/i
        }),
        createItem('Social Posts', '/posts', '', {
            regex: /\/posts/i
        }),
        createItem('Post Tags', '/post-tags', '', {
            regex: /\/post-tags/i
        }),
        createItem('Posted URLs', '/posted-urls', '', {
            regex: /\/posted-urls/i
        })
    ];

    configurations.CROSS_CHANNEL = createConfiguration('Cross-Channel', {
        channel: 'cross_channel'
    }, crossChannelItems);
    addSourcesToConfiguration(configurations.CROSS_CHANNEL);

    configurations.ADMIN = createConfiguration('Admin', null, [
        createItem('Users', '#/users', '/admin', {
            regex: /users|invoices|accountDetails/i,
            data: {
                target: '#tabUsers'
            }
        }),
        createItem('Companies', '#/companies', '/admin', {
            data: {
                target: '#tabEditing'
            }
        }),
        createItem('Queries', '#/queries', '/admin', {
            data: {
                target: '#tabQueries'
            }
        })
    ]);

    configurations.REPORTS = createConfiguration('Reports', null, [
        createItem('Reports', '/reports', null),
        createItem('Scheduled Exports', '/reports/scheduled', null)
    ]);

    const companyBaseUrl = /^\/company\/\d+/;

    configurations.COMPANY_PROFILE = createConfiguration('Company', null, [
        createItem('Overview', '', companyBaseUrl),
        createItem('Social Posts', '/posts', companyBaseUrl, {
            regex: /\/posts/i
        }),
        createItem('Alert Feed', '/feed', companyBaseUrl, {
            regex: /\/feed/i
        })
    ]);

    function createDisabledForLimitedItem(name, url, baseUrl, navOptions, options) {
        return createItem(name, url, baseUrl, navOptions, _.defaults({
            viewOptions: {
                className: function () {
                    return (RivalIQ.state.account.hasLimitedLandscapeCapability() ? 'disabled' : '');
                }
            }
        }, options));
    }

    configurations.LANDSCAPE = createConfiguration('Landscape', null, [
        createItem('Companies', '', '/companies'),
        createDisabledForLimitedItem('Manage Channels', '#actionsTab', '/settings'),
        createDisabledForLimitedItem('Configure Alerts', '#emailTab', '/settings'),
        createDisabledForLimitedItem('Customize Branding', '#themeTab', '/settings')
    ]);

    configurations.COMPANIES = createConfiguration('Companies', null, [
        createItem('Companies', '', '/companies')
    ]);

    configurations.LANDSCAPE_SETTINGS = createConfiguration('Landscape Settings', null, [
        createDisabledForLimitedItem('Actions', '#actionsTab', '/settings'),
        createDisabledForLimitedItem('Alerts', '#emailTab', '/settings'),
        createDisabledForLimitedItem('Custom Branding', '#themeTab', '/settings')
    ]);

    const accountSettingItems = [
        createItem('Profile', '/account/user'),
        createConditionalItem('Subscription', '#subscription', '/manage', {}, null, can('update', RivalIQ.state.account)),
        createItem('Company Usage', '/account/companies'),
        createItem('Listening Usage', '/account/listening'),
        createConditionalItem('Receipts', '/account/receipts', null, {
            regex: /^\/account\/receipts(\/d+)?/
        }, null, can('update', RivalIQ.state.account)),
        createConditionalItem('Team', '/account/users', null, {}, null, can('update', RivalIQ.state.account)),
        createConditionalItem('Connected Accounts', '/account/services', null, {}, null, can('update', RivalIQ.state.account)),
        createItem('Payment Entry', '#payment-entry', '/manage', null, {
            'class': 'hidden'
        })
    ];

    configurations.ACCOUNT_SETTINGS = createConfiguration('Account Settings', null, accountSettingItems);

    function can(action, resource) {
        return function () {
            return RivalIQ.state.user.can(action, resource);
        };
    }

    configurations.DASHBOARD = createConfiguration('Dashboard');

    configurations.REPORT_CENTER = createConfiguration('Downloads');

    configurations.HELP = createConfiguration('FAQ / Help');

    /**
     * Gets a configuration by source.
     * @param source
     * @returns {*}
     */
    menus.getPageBySource = function (source) {

        const page = _.find(configurations, function (value) {

            const metadata = value.metadata;
            return (metadata && metadata.source === source);
        });

        return page;
    };

    /**
     * Gets a configuration by channel.
     * @param channel
     * @returns {*}
     */
    menus.getPageByChannel = function (channel) {
        channel = channel || 'cross_channel';

        const page = _.find(configurations, function (value) {

            const metadata = value.metadata;
            return (metadata && metadata.channel === channel);
        });

        return page;
    };

    /**
     * Gets a configuration by name.
     * @param name
     * @returns {*}
     */
    menus.getPageByName = function (name) {

        return _.find(configurations, {
            name: name
        });
    };

    /**
     * Gets the configurations.
     * @returns {{}}
     */
    menus.getConfiguration = function () {

        return configurations;
    };

}, window, RivalIQ.enums);
